import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Stopwatch } from '@/components/Timer';
import { QuestionPalette } from '@/components/QuestionPalette';
import { DiagramBlock } from '@/components/DiagramBlock';
import { GraduationCap, Flag, ChevronLeft, ChevronRight, Menu, Send } from 'lucide-react';

export default function TestScreenPage() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const location = useLocation();

  const [session, setSession] = useState(location.state?.session || null);
  const [loading, setLoading] = useState(!location.state?.session);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // { index: optionIndex }
  const [marked, setMarked] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (session) return;
    (async () => {
      try {
        const { data } = await api.get(`/test/${sessionId}`);
        if (data.status === 'submitted') {
          nav(`/report/${sessionId}`, { replace: true });
          return;
        }
        setSession(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Session not found');
        nav('/dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [session, sessionId, nav]);

  const questions = session?.questions || [];
  const total = questions.length;
  const q = questions[current];

  const answeredCount = useMemo(
    () => Object.values(answers).filter((v) => v !== undefined && v !== null).length,
    [answers]
  );

  const setAnswer = async (index, option) => {
    setAnswers((prev) => ({ ...prev, [index]: option }));
    const qid = questions[index]?.id;
    if (!qid) return;
    try {
      await api.post(`/test/${sessionId}/answer`, {
        question_id: qid,
        option_index: option,
      });
    } catch {
      // non-fatal; will still submit final answers on submit
    }
  };

  const toggleMark = (index) => {
    setMarked((prev) => {
      const nx = new Set(prev);
      if (nx.has(index)) nx.delete(index);
      else nx.add(index);
      return nx;
    });
  };

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      // Send final answers keyed by question_id
      const finalAnswers = {};
      Object.entries(answers).forEach(([idx, opt]) => {
        const qid = questions[Number(idx)]?.id;
        if (qid !== undefined && opt !== undefined && opt !== null) {
          finalAnswers[qid] = opt;
        }
      });
      await api.post(`/test/${sessionId}/submit`, { answers: finalAnswers });
      if (auto) toast.info("Time's up! Auto-submitted.");
      else toast.success('Submitted — preparing your report card…');
      nav(`/report/${sessionId}`);
    } catch (e) {
      submittedRef.current = false;
      toast.error(e?.response?.data?.detail || 'Submit failed');
      setSubmitting(false);
    }
  };

  if (loading || !session) {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">Loading test…</div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-card">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="truncate font-display text-sm font-semibold">
                {session.subject} · {session.chapter}
              </div>
              <div className="text-[11px] text-muted-foreground">
                Set {session.set_number} · Q {current + 1} / {total} · Answered {answeredCount}/{total}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Stopwatch startISO={session.start_time} />
            <Sheet open={paletteOpen} onOpenChange={setPaletteOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 lg:hidden" data-testid="question-palette-open">
                  <Menu className="h-4 w-4" />
                  Palette
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px]">
                <SheetHeader>
                  <SheetTitle>Question palette</SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                  <QuestionPalette
                    total={total}
                    currentIndex={current}
                    answers={answers}
                    marked={marked}
                    onJump={(i) => {
                      setCurrent(i);
                      setPaletteOpen(false);
                    }}
                  />
                </div>
              </SheetContent>
            </Sheet>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive" className="gap-1.5" data-testid="submit-test-button">
                  <Send className="h-4 w-4" />
                  Submit
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Submit test?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Answered: {answeredCount} / {total}. Marked: {marked.size}. Unanswered: {total - answeredCount}.
                    You cannot change answers after submitting.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="submit-cancel">Continue test</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => submit(false)}
                    disabled={submitting}
                    data-testid="submit-confirm"
                  >
                    {submitting ? 'Submitting…' : 'Yes, submit'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-testid="test-page">
        <Card>
          <CardContent className="space-y-5 p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">Q{current + 1} / {total}</Badge>
              {q?.source_style && <Badge variant="outline">{q.source_style}</Badge>}
              {q?.difficulty && <Badge variant="outline" className="capitalize">{q.difficulty}</Badge>}
              {q?.diagram?.type === 'svg' && <Badge variant="outline">Diagram</Badge>}
              {q?.topic_tag && <Badge variant="outline">{q.topic_tag}</Badge>}
            </div>

            <div className="text-base leading-relaxed sm:text-lg" data-testid="question-stem">
              {q?.stem}
            </div>

            <DiagramBlock diagram={q?.diagram} />

            <RadioGroup
              value={answers[current] !== undefined ? String(answers[current]) : ''}
              onValueChange={(v) => setAnswer(current, Number(v))}
              className="space-y-2"
            >
              {q?.options?.map((opt, i) => {
                const selected = answers[current] === i;
                return (
                  <Label
                    key={i}
                    htmlFor={`opt-${current}-${i}`}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-secondary/60'
                    }`}
                    data-testid={`option-${['a', 'b', 'c', 'd'][i]}`}
                  >
                    <RadioGroupItem
                      id={`opt-${current}-${i}`}
                      value={String(i)}
                      className="mt-0.5"
                    />
                    <span className="text-sm sm:text-base">
                      <span className="mr-2 font-semibold text-muted-foreground">{'ABCD'[i]}.</span>
                      {opt}
                    </span>
                  </Label>
                );
              })}
            </RadioGroup>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrent((i) => Math.max(0, i - 1))}
                  disabled={current === 0}
                  data-testid="prev-question-button"
                  className="gap-1.5"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <Button
                  variant={marked.has(current) ? 'default' : 'ghost'}
                  onClick={() => toggleMark(current)}
                  data-testid="mark-for-review-button"
                  className="gap-1.5"
                >
                  <Flag className="h-4 w-4" />
                  {marked.has(current) ? 'Unmark' : 'Mark for review'}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAnswer(current, null);
                  }}
                  data-testid="clear-response-button"
                >
                  Clear response
                </Button>
                <Button
                  onClick={() => setCurrent((i) => Math.min(total - 1, i + 1))}
                  disabled={current === total - 1}
                  data-testid="next-question-button"
                  className="gap-1.5"
                >
                  Save & Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <aside className="hidden lg:block">
          <Card className="sticky top-24">
            <CardContent className="p-4">
              <QuestionPalette
                total={total}
                currentIndex={current}
                answers={answers}
                marked={marked}
                onJump={setCurrent}
              />
            </CardContent>
          </Card>
        </aside>
      </main>
    </div>
  );
}
